/*
  nytstnd-rotating-card
  ---------------------
  Hover photo-rotation for product cards WITHOUT a color group — i.e. single
  products such as "TRIO Flex Qi2". On hover it cycles through the product's
  own photos with the same story-style progress bar as grouped color cards.

  Grouped color cards (which rotate the SELECTED color's media) are handled
  separately by nytstnd-grouped-card.js. This element wraps the image area so
  it auto-initializes even when the collection grid is re-rendered by filters.
*/
if (!customElements.get('nytstnd-rotating-card')) {
  customElements.define(
    'nytstnd-rotating-card',
    class extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this.rotatingImages = Array.from(this.querySelectorAll('.card__rotating-image'));
        if (!this.rotatingImages.length) return;
        this.progressBar = this.querySelector('.card__rotation-progress');
        this.rotateInterval = parseInt(this.dataset.rotateInterval, 10) || 2000;

        this.addEventListener('mouseenter', () => this.startRotation());
        this.addEventListener('mouseleave', () => this.stopRotation());
        this._wired = true;
      }

      // Frames in order: the currently-visible primary image, then the extras.
      // Re-reading the primary each time keeps frame 0 correct if a variant
      // swatch has swapped the visible image.
      frames() {
        const primary =
          this.querySelector('.card__primary-image:not([hidden])') ||
          this.querySelector('.card__primary-image');
        const extras = this.rotatingImages
          .slice()
          .sort((a, b) => Number(a.dataset.rotateIndex) - Number(b.dataset.rotateIndex));
        return [primary, ...extras].filter(Boolean);
      }

      startRotation() {
        const frames = this.frames();
        if (frames.length <= 1) return;
        // Reveal the extra frames so the browser loads them (they begin hidden)
        frames.forEach((img) => {
          if (img.classList.contains('card__rotating-image')) {
            img.removeAttribute('hidden');
            img.setAttribute('loading', 'eager');
          }
        });
        this.buildProgress(frames.length);
        this.updateProgress(0);
        let i = 0;
        clearInterval(this._timer);
        this._timer = setInterval(() => {
          i = (i + 1) % frames.length;
          this.showFrame(frames, i);
          this.updateProgress(i);
        }, this.rotateInterval);
      }

      showFrame(frames, i) {
        this.rotatingImages.forEach((img) => img.classList.remove('card__rotating-image--current'));
        const current = frames[i];
        if (current && current.classList.contains('card__rotating-image')) {
          current.classList.add('card__rotating-image--current');
        }
      }

      // Story-style segmented progress bar — one segment per frame
      buildProgress(count) {
        if (!this.progressBar) return;
        this.progressBar.innerHTML = '';
        this.progressSegments = [];
        for (let n = 0; n < count; n += 1) {
          const seg = document.createElement('span');
          seg.className = 'card__rotation-progress-segment';
          const fill = document.createElement('span');
          fill.className = 'card__rotation-progress-fill';
          seg.appendChild(fill);
          this.progressBar.appendChild(seg);
          this.progressSegments.push(seg);
        }
        this.progressBar.classList.add('is-visible');
      }

      updateProgress(i) {
        if (!this.progressSegments) return;
        this.progressSegments.forEach((seg, idx) => {
          const fill = seg.firstChild;
          if (idx < i) {
            fill.style.transition = 'none';
            fill.style.width = '100%';
          } else if (idx > i) {
            fill.style.transition = 'none';
            fill.style.width = '0%';
          }
        });
        const active = this.progressSegments[i];
        if (active) {
          const fill = active.firstChild;
          fill.style.transition = 'none';
          fill.style.width = '0%';
          void fill.offsetWidth; // force reflow so the transition restarts
          fill.style.transition = 'width ' + this.rotateInterval + 'ms linear';
          fill.style.width = '100%';
        }
      }

      stopRotation() {
        clearInterval(this._timer);
        this._timer = null;
        if (this.rotatingImages) {
          this.rotatingImages.forEach((img) => {
            img.classList.remove('card__rotating-image--current');
            img.setAttribute('hidden', 'true');
          });
        }
        if (this.progressBar) {
          this.progressBar.innerHTML = '';
          this.progressBar.classList.remove('is-visible');
          this.progressSegments = null;
        }
      }
    }
  );
}
